/**
 * HelixHorizon.tsx
 *
 * W66.C — Methodology Workspace — Horizon 4: Helix View
 *
 * Preview panel for the M-PACT Helix DNA health model (/helix, /opportunity-dna).
 *
 * OperationalHelix requires a mandatory accountId prop and live localStorage
 * answer data. Embedding it without an account context renders an empty
 * diagnostic surface. Thumbnail + CTA used per W66.C HARD STOP rule.
 *
 * CANON: No exclamation points. No emojis. M-PACT hyphenated.
 */

import { Link } from "wouter";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// ─── Locked health palette (canon §5 — five colors only) ────────────────────
const HEALTH = {
  green: "#16A34A",
  yellow: "#EAB308",
  orange: "#EA580C",
  red: "#DC2626",
  gray: "#6B7280",
};

// ─── Static helix orb strip ───────────────────────────────────────────────────

function HelixStrip() {
  // Demo health state to show the diagnostic range
  const DEMO_HEALTH: Array<keyof typeof HEALTH> = [
    "green",
    "green",
    "yellow",
    "orange",
    "green",
    "red",
    "yellow",
    "green",
    "green",
    "gray",
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        position: "relative",
        padding: "8px 0",
      }}
    >
      {/* Spine */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 2,
          background: "rgba(107,114,128,0.15)",
          transform: "translateX(-50%)",
          zIndex: 0,
        }}
      />

      {HELIX_CHECKPOINTS.map((cp, idx) => {
        const health = DEMO_HEALTH[idx];
        const color = HEALTH[health];
        const side = idx % 2 === 0 ? "left" : "right";

        return (
          <div
            key={cp.id}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 8,
              flexDirection: side === "left" ? "row" : "row-reverse",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                flex: 1,
                textAlign: side === "left" ? "right" : "left",
                fontSize: 10,
                color: "rgba(255,255,255,0.5)",
                whiteSpace: "nowrap",
              }}
            >
              CP{cp.number}
            </div>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: color,
                border: "2px solid rgba(255,255,255,0.15)",
                boxShadow: `0 0 8px ${color}66`,
                flexShrink: 0,
              }}
            />
            <div
              style={{
                flex: 1,
                textAlign: side === "right" ? "right" : "left",
                fontSize: 10,
                lineHeight: 1.25,
                color: "rgba(255,255,255,0.45)",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {cp.name}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Horizon component ────────────────────────────────────────────────────────

export default function HelixHorizon() {
  return (
    <section>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        className="methodology-helix-grid"
      >
        {/* Left — static helix preview */}
        <div
          style={{
            background: "#0A0A0C",
            borderRadius: 10,
            padding: "24px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.35)",
              marginBottom: 6,
            }}
          >
            Helix health model — demo state
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", marginBottom: 16 }}>
            Live orb colors render from your account answer data
          </div>
          <HelixStrip />
          {/* Health legend */}
          <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { color: HEALTH.green, label: "Good" },
              { color: HEALTH.yellow, label: "Caution" },
              { color: HEALTH.orange, label: "Stalled" },
              { color: HEALTH.red, label: "Bad" },
              { color: HEALTH.gray, label: "Not scored" },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — description + CTAs */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 10,
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginBottom: 10,
              }}
            >
              About this view
            </div>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0B0B0F",
                margin: "0 0 10px",
                letterSpacing: "-0.01em",
              }}
            >
              Opportunity DNA — Helix Health Model
            </h3>
            <p style={{ fontSize: 13, color: "#3D3D42", lineHeight: 1.65, margin: "0 0 16px" }}>
              The Helix model is a DNA diagnostic visualization that maps each checkpoint
              to a health orb on the double-helix spine. Orb color reflects the strength
              of the answers at that checkpoint — from Strong through Early. The 3D helix
              rotates, responds to orb clicks, and opens the full Q&A panel for that
              checkpoint inline.
            </p>
            <ul style={{ margin: "0 0 20px", paddingLeft: 18, fontSize: 12, color: "#6B6B70", lineHeight: 1.7 }}>
              <li>Three-zone layout: 3D helix · CP legend · Q&A panel</li>
              <li>Orb click opens the Q&A panel scoped to that checkpoint</li>
              <li>Two-way sync: legend click orients the helix; answer save updates orb color</li>
              <li>Account-scoped: answers saved per account in local storage with Supabase sync</li>
              <li>Read-only and editable modes available depending on your role</li>
            </ul>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link
              href="/helix"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#0B0B0F",
                color: "#FAFAF7",
                borderRadius: 8,
                padding: "11px 20px",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Open full view — Helix DNA
            </Link>
            <Link
              href="/opportunity-dna"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#FFFFFF",
                color: "#0B0B0F",
                border: "1px solid #E5E5E7",
                borderRadius: 8,
                padding: "9px 20px",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Opportunity DNA — /opportunity-dna
            </Link>
            <p style={{ fontSize: 11, color: "#9CA3AF", margin: 0 }}>
              Direct links: /helix · /opportunity-dna
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .methodology-helix-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
